/**
 * Client – My Coaching Plan: workouts (calendar + mark done), nutrition, progress.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { getMyCoachId, getCoach } from '../../domain/usecases/coaching';
import {
  getMySchedule,
  markWorkoutCompleted,
  getMyCompletions,
  isWorkoutCompleted,
} from '../../domain/usecases/schedule';
import { getMyNutritionPlan } from '../../domain/usecases/coaching';
import Card from '../../shared/ui/Card.jsx';
import Badge from '../../shared/ui/Badge.jsx';
import Button from '../../shared/ui/Button.jsx';
import EmptyState from '../../shared/ui/EmptyState.jsx';
import Stat from '../../shared/ui/Stat.jsx';
import ProgressBar from '../../shared/ui/ProgressBar.jsx';
import Display from '../../shared/ui/Display.jsx';
import Title from '../../shared/ui/Title.jsx';
import Body from '../../shared/ui/Body.jsx';
import Label from '../../shared/ui/Label.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import styles from './MyCoachingPlanPage.module.css';

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function getTodayStr() {
  return new Date().toISOString().slice(0, 10);
}

function getWeekRange() {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - 7);
  const end = new Date(today);
  end.setDate(today.getDate() + 7);
  return {
    from: start.toISOString().slice(0, 10),
    to: end.toISOString().slice(0, 10),
  };
}

export function MyCoachingPlanPage() {
  const { userId } = useAuth();
  const [coachId, setCoachId] = useState(null);
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState({ schedule: null, days: [] });
  const [selectedDate, setSelectedDate] = useState(getTodayStr());
  const [completions, setCompletions] = useState([]);
  const [nutrition, setNutrition] = useState(null);
  const [tab, setTab] = useState('workouts');
  const [actingId, setActingId] = useState(null);

  useEffect(() => {
    if (!userId) return;
    getMyCoachId(userId).then((id) => {
      const resolvedId = id || null;
      setCoachId(resolvedId);
      setLoading(false);
      if (resolvedId) {
        getCoach(resolvedId).then((doc) => setCoach(doc || null)).catch(() => {});
      }
    });
  }, [userId]);

  // Fetch schedule whenever userId, coachId, or selectedDate changes.
  useEffect(() => {
    if (!userId || !coachId) return;
    const { from, to } = getWeekRange();
    getMySchedule(userId, from, to).then(setSchedule);
  }, [userId, coachId, selectedDate]);

  useEffect(() => {
    if (!userId || !coachId) return;
    getMyCompletions(userId).then(setCompletions);
    getMyNutritionPlan(userId).then(setNutrition);
  }, [userId, coachId]);

  async function handleMarkDone(assignedWorkoutId) {
    if (!userId) return;
    setActingId(assignedWorkoutId);
    try {
      await markWorkoutCompleted(userId, assignedWorkoutId);
      // Optimistically add the completion so the badge appears immediately.
      setCompletions((prev) =>
        prev.some((c) => c.assignedWorkoutId === assignedWorkoutId)
          ? prev
          : [...prev, { assignedWorkoutId, clientId: userId, completedAt: new Date() }]
      );
    } finally {
      setActingId(null);
    }
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <Display as="h1" size="l" style={{ marginBottom: '1.5rem' }}>My coaching plan</Display>
        <div className={styles.skeletonList}>
          {[1, 2, 3].map((i) => <Skeleton key={i} width="100%" height={80} radius="var(--radius-md)" />)}
        </div>
      </div>
    );
  }

  if (!coachId) {
    return (
      <div className={styles.page}>
        <Display as="h1" size="l" style={{ marginBottom: '1.5rem' }}>My coaching plan</Display>
        <EmptyState
          icon="coach"
          title="No active plan"
          body="You don't have an assigned coach yet. Browse coaches and send a training request."
          action={
            <Button variant="primary" size="lg" as={Link} to="/coaching">
              Find a coach
            </Button>
          }
        />
      </div>
    );
  }

  const selectedDayData = schedule.days.find((d) => d.day.date === selectedDate);
  const assignedList = selectedDayData?.assigned ?? [];

  const isCoachSuspended =
    coach && (coach.isActiveCoach === false || coach.approvalStatus !== 'approved');

  return (
    <div className={styles.page}>
      <Display as="h1" size="l" style={{ marginBottom: '1.5rem' }}>My coaching plan</Display>

      {isCoachSuspended && (
        <Card style={{ marginBottom: '1.25rem' }} frame="gold">
          <Body>
            Your coach is no longer active. You can continue viewing past programs, but new
            assignments are paused.
          </Body>
        </Card>
      )}

      <div className={styles.tabs}>
        <button
          type="button"
          className={tab === 'workouts' ? styles.tabActive : styles.tab}
          onClick={() => setTab('workouts')}
        >
          My Workouts
        </button>
        <button
          type="button"
          className={tab === 'nutrition' ? styles.tabActive : styles.tab}
          onClick={() => setTab('nutrition')}
        >
          My Nutrition
        </button>
        <button
          type="button"
          className={tab === 'progress' ? styles.tabActive : styles.tab}
          onClick={() => setTab('progress')}
        >
          My Progress
        </button>
      </div>

      {tab === 'workouts' && (
        <>
          <div className={styles.dateNav}>
            <Body as="label" tone="secondary" style={{ display: 'block', fontSize: 13, marginBottom: '0.35rem' }}>
              Date
            </Body>
            <input
              type="date"
              className={styles.dateInput}
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
          {assignedList.length === 0 ? (
            <EmptyState
              icon="calendar"
              title="No workouts"
              body="No workouts assigned for this day."
            />
          ) : (
            <ul className={styles.workoutList}>
              {assignedList.map(({ assigned, workout }) => {
                const done = isWorkoutCompleted(completions, assigned.id);
                const completion = completions.find((c) => c.assignedWorkoutId === assigned.id);
                return (
                  <li key={assigned.id}>
                    <Card className={styles.workoutCard}>
                      <div className={styles.workoutMain}>
                        <Title as="h3" style={{ fontSize: 16, lineHeight: '22px', marginBottom: 4 }}>
                          {workout.title || 'Workout'}
                        </Title>
                        {(workout.durationMinutes > 0 || workout.sets != null || workout.reps != null) && (
                          <Body tone="secondary" as="p" style={{ fontSize: 13, margin: '0 0 4px' }}>
                            {workout.durationMinutes > 0 && `${workout.durationMinutes} min`}
                            {workout.sets != null && ` · ${workout.sets} sets`}
                            {workout.reps != null && ` · ${workout.reps} reps`}
                          </Body>
                        )}
                        {workout.videoUrl && (
                          <a href={workout.videoUrl} target="_blank" rel="noopener noreferrer" className={styles.videoLink}>
                            Watch video →
                          </a>
                        )}
                        {completion?.coachNote && (
                          <div style={{ marginTop: 8, padding: '6px 10px', borderLeft: '2px solid var(--gold)', background: 'var(--bg-elevated)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0' }}>
                            <Label style={{ fontSize: 10, letterSpacing: '0.08em', color: 'var(--gold)', display: 'block', marginBottom: 2 }}>
                              COACH NOTE
                            </Label>
                            <Body tone="secondary" style={{ fontSize: 13 }}>{completion.coachNote}</Body>
                          </div>
                        )}
                      </div>
                      {done ? (
                        <Badge variant="success">✓ Done</Badge>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={actingId === assigned.id}
                          onClick={() => handleMarkDone(assigned.id)}
                        >
                          {actingId === assigned.id ? 'Saving…' : 'Mark done'}
                        </Button>
                      )}
                    </Card>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {tab === 'nutrition' && (
        <>
          {!nutrition || (!nutrition.caloriesTarget && !nutrition.mealPlan && !nutrition.pdfUrl) ? (
            <EmptyState
              icon="user"
              title="No nutrition plan"
              body="Your coach hasn't set a nutrition plan yet."
            />
          ) : (
            <Card>
              {(nutrition.caloriesTarget != null || nutrition.protein != null) && (
                <section className={styles.macros}>
                  <Title as="h3" style={{ fontSize: 16, marginBottom: 12 }}>Macros</Title>
                  <div className={styles.macroStats}>
                    {nutrition.caloriesTarget != null && (
                      <Stat label="CALORIES" value={`${nutrition.caloriesTarget} kcal`} />
                    )}
                    {nutrition.protein != null && (
                      <Stat label="PROTEIN" value={`${nutrition.protein}g`} />
                    )}
                    {nutrition.carbs != null && (
                      <Stat label="CARBS" value={`${nutrition.carbs}g`} />
                    )}
                    {nutrition.fats != null && (
                      <Stat label="FATS" value={`${nutrition.fats}g`} />
                    )}
                  </div>
                </section>
              )}
              {nutrition.notes && (
                <Body tone="secondary" as="p" style={{ marginTop: 12 }}>{nutrition.notes}</Body>
              )}
              {nutrition.pdfUrl && (
                <a href={nutrition.pdfUrl} target="_blank" rel="noopener noreferrer" className={styles.pdfLink}>
                  Download nutrition PDF →
                </a>
              )}
            </Card>
          )}
        </>
      )}

      {tab === 'progress' && (
        <Card>
          <Title as="h3" style={{ fontSize: 16, marginBottom: 16 }}>Completed workouts</Title>
          <Stat label="WORKOUTS LOGGED" value={completions.length} />
          <Body tone="secondary" as="p" style={{ marginTop: 12 }}>
            Keep it up! Your coach can see your progress.
          </Body>
        </Card>
      )}
    </div>
  );
}
